import { Component, ChangeDetectionStrategy, input, output, inject, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { catchError, finalize, of } from 'rxjs';
import { SaleService } from '../../services/sale.service';
import type {
  CollectionMethodEnumValue,
  FrequencyEnumValue,
  InvoiceStatusEnumValue,
  SaleDTO,
  SubscriptionDTO,
  SubscriptionStatusEnumValue,
  WrapperPageableResponseSaleDTO
} from '../../core/models';
import { CollectionMethodEnum } from '../../core/models';
import { formatApiDateOnly } from '../../core/date-utils';

@Component({
  selector: 'app-subscription-read',
  templateUrl: './subscription-read.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, RouterLink, ButtonModule]
})
export class SubscriptionReadComponent {
  protected readonly formatApiDateOnly = formatApiDateOnly;

  subscription = input.required<SubscriptionDTO>();
  openSale = output<number>();
  openCustomer = output<number>();

  private saleService = inject(SaleService);

  sales = signal<SaleDTO[]>([]);
  salesLoading = signal(false);
  salesLoadingMore = signal(false);
  salesHasMore = signal(false);

  private salesPage = 0;
  private readonly salesPageSize = 5;

  private readonly saleStatusOptions: { value: InvoiceStatusEnumValue; label: string }[] = [
    { value: 'DRAFT', label: 'Borrador' },
    { value: 'ISSUED', label: 'Emitida' },
    { value: 'PARTIALLY_PAID', label: 'Parcialmente pagada' },
    { value: 'PAID', label: 'Pagada' },
    { value: 'VOID', label: 'Anulada' },
    { value: 'UNCOLLECTIBLE', label: 'Incobrable' }
  ];

  private readonly statusOptions: { value: SubscriptionStatusEnumValue; label: string }[] = [
    { value: 'PENDING_PAYMENT', label: 'Pendiente de pago' },
    { value: 'ACTIVE', label: 'Activa' },
    { value: 'PAUSED', label: 'Pausada' },
    { value: 'CANCELLED', label: 'Cancelada' },
    { value: 'EXPIRED', label: 'Expirada' }
  ];

  private readonly frequencyOptions: { value: FrequencyEnumValue; label: string }[] = [
    { value: 'ONCE', label: 'Una vez' },
    { value: 'DAILY', label: 'Diario' },
    { value: 'WEEKLY', label: 'Semanal' },
    { value: 'BIWEEKLY', label: 'Quincenal' },
    { value: 'MONTHLY', label: 'Mensual' },
    { value: 'QUARTERLY', label: 'Trimestral' },
    { value: 'SEMIANNUALLY', label: 'Semestral' },
    { value: 'ANNUALLY', label: 'Anual' }
  ];

  private readonly collectionMethodLabels: Record<CollectionMethodEnumValue, string> = {
    [CollectionMethodEnum.LINK]: 'Liga de pago',
    [CollectionMethodEnum.RECURRING_CHARGE]: 'Cobro recurrente',
    [CollectionMethodEnum.MANUAL]: 'Manual',
    [CollectionMethodEnum.MERCHANT_INITIATED]: 'Cobro bajo demanda'
  };

  constructor() {
    effect(() => {
      const subscriptionId = this.subscription()?.id;
      if (subscriptionId != null) {
        this.resetAndLoadSales(subscriptionId);
      } else {
        this.sales.set([]);
        this.salesHasMore.set(false);
      }
    });
  }

  loadMoreSales(): void {
    const subscriptionId = this.subscription()?.id;
    if (subscriptionId == null || this.salesLoadingMore() || !this.salesHasMore()) return;
    this.salesPage++;
    this.fetchSales(subscriptionId, false);
  }

  openSaleDetails(sale: SaleDTO): void {
    if (sale?.id != null) {
      this.openSale.emit(sale.id);
    }
  }

  openCustomerDetails(): void {
    const customerId = this.subscription()?.customer?.id;
    if (customerId != null) {
      this.openCustomer.emit(customerId);
    }
  }

  getStatusLabel(status: SubscriptionStatusEnumValue | string | undefined): string {
    if (!status) return '—';
    return this.statusOptions.find((o) => o.value === status)?.label ?? String(status);
  }

  getStatusIcon(status: SubscriptionStatusEnumValue | string | undefined): string {
    switch (status) {
      case 'PENDING_PAYMENT': return 'pi pi-clock';
      case 'ACTIVE': return 'pi pi-check-circle';
      case 'PAUSED': return 'pi pi-pause';
      case 'CANCELLED': return 'pi pi-ban';
      case 'EXPIRED': return 'pi pi-calendar-times';
      default: return 'pi pi-sync';
    }
  }

  getStatusIconClasses(status: SubscriptionStatusEnumValue | string | undefined, compact = false): string {
    const size = compact ? 'h-7 w-7' : 'h-10 w-10';
    const base = `shrink-0 ${size} rounded-full flex items-center justify-center`;
    switch (status) {
      case 'PENDING_PAYMENT': return `${base} bg-amber-100 text-amber-600`;
      case 'ACTIVE': return `${base} bg-emerald-100 text-emerald-600`;
      case 'PAUSED': return `${base} bg-sky-100 text-sky-600`;
      case 'CANCELLED': return `${base} bg-red-100 text-red-600`;
      case 'EXPIRED': return `${base} bg-slate-200 text-slate-600`;
      default: return `${base} bg-slate-100 text-slate-600`;
    }
  }

  getFrequencyLabel(freq: FrequencyEnumValue | string | undefined): string {
    if (!freq) return '—';
    return this.frequencyOptions.find((o) => o.value === freq)?.label ?? String(freq);
  }

  getCollectionMethodLabel(method: CollectionMethodEnumValue | string | undefined): string {
    if (!method) return '—';
    return this.collectionMethodLabels[method as CollectionMethodEnumValue] ?? String(method);
  }

  getSaleStatusLabel(status: InvoiceStatusEnumValue | string | undefined): string {
    if (!status) return '—';
    return this.saleStatusOptions.find((o) => o.value === status)?.label ?? String(status);
  }

  getSaleStatusIcon(status: InvoiceStatusEnumValue | string | undefined): string {
    if (!status) return 'pi pi-receipt';
    switch (status) {
      case 'DRAFT':
        return 'pi pi-file-edit';
      case 'ISSUED':
        return 'pi pi-send';
      case 'PARTIALLY_PAID':
        return 'pi pi-wallet';
      case 'PAID':
        return 'pi pi-check-circle';
      case 'VOID':
        return 'pi pi-ban';
      case 'UNCOLLECTIBLE':
        return 'pi pi-exclamation-triangle';
      default:
        return 'pi pi-receipt';
    }
  }

  getSaleStatusIconClasses(status: InvoiceStatusEnumValue | string | undefined, compact = false): string {
    const size = compact ? 'h-7 w-7' : 'h-10 w-10';
    const base = `shrink-0 ${size} rounded-full flex items-center justify-center`;
    if (!status) return `${base} bg-slate-100 text-slate-600`;
    switch (status) {
      case 'DRAFT':
        return `${base} bg-sky-100 text-sky-600`;
      case 'ISSUED':
        return `${base} bg-violet-100 text-violet-600`;
      case 'PARTIALLY_PAID':
        return `${base} bg-amber-100 text-amber-600`;
      case 'PAID':
        return `${base} bg-emerald-100 text-emerald-600`;
      case 'VOID':
        return `${base} bg-slate-200 text-slate-600`;
      case 'UNCOLLECTIBLE':
        return `${base} bg-red-100 text-red-600`;
      default:
        return `${base} bg-slate-100 text-slate-600`;
    }
  }

  private resetAndLoadSales(subscriptionId: number): void {
    this.salesPage = 0;
    this.sales.set([]);
    this.salesHasMore.set(false);
    this.fetchSales(subscriptionId, true);
  }

  private fetchSales(subscriptionId: number, reset: boolean): void {
    if (reset) {
      this.salesLoading.set(true);
    } else {
      this.salesLoadingMore.set(true);
    }

    this.saleService
      .searchSales({ subscriptionId }, { page: this.salesPage, size: this.salesPageSize, sort: ['createdAt,desc'] })
      .pipe(
        catchError(() => of({ ok: false, body: [] as SaleDTO[] })),
        finalize(() => {
          this.salesLoading.set(false);
          this.salesLoadingMore.set(false);
        })
      )
      .subscribe((response) => {
        const items = response.ok && response.body ? response.body : [];
        if (reset) {
          this.sales.set(items);
        } else {
          this.sales.update((prev) => [...prev, ...items]);
        }

        const page = (response as WrapperPageableResponseSaleDTO).page;
        const currentPage = page?.currentPage ?? this.salesPage + 1;
        const totalPages = page?.totalPages ?? 0;
        this.salesHasMore.set(currentPage < totalPages);
      });
  }
}
